import { useCallback, useEffect, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { PixType } from '../../enums';
import { pixKeysApi } from '../../services/pixKeysApi';
import { unformatValue } from '../../utils/pixFormatters';
const COUNTDOWN_SECONDS = 60;
export function usePixKeyResend({ key, keyType, protocolId, onError, onResendSuccess, }) {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag(undefined, { autoLoad: false });
    const isBaasV2 = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const [timeRemaining, setTimeRemaining] = useState(COUNTDOWN_SECONDS);
    const [canResend, setCanResend] = useState(false);
    const [resendPixKeyCode, { isLoading: isV1Loading }] = pixKeysApi.useResendPixKeyCodeMutation();
    const [resendPixKeyCodeV2, { isLoading: isV2Loading }] = pixKeysApi.useResendPixKeyCodeV2Mutation();
    const isResendLoading = isV1Loading || isV2Loading;
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    setCanResend(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);
    const handleResendCode = useCallback(async () => {
        if (!canResend || isResendLoading)
            return;
        try {
            let formattedKey = key;
            if (keyType === PixType.PHONE) {
                formattedKey = key.startsWith('+55') ? `+55${unformatValue(key.substring(3))}` : `+55${unformatValue(key)}`;
            }
            const resendFn = isBaasV2 ? resendPixKeyCodeV2 : resendPixKeyCode;
            const response = await resendFn({
                key: formattedKey,
                request: { protocolId },
            }).unwrap();
            const newProtocolId = response?.result?.protocolId;
            if (newProtocolId && onResendSuccess) {
                onResendSuccess(newProtocolId);
            }
            setTimeRemaining(COUNTDOWN_SECONDS);
            setCanResend(false);
        }
        catch (error) {
            const errorMessage = error?.data?.message ?? t('pix-keys.confirmation.resend-error');
            onError(errorMessage);
        }
    }, [
        canResend,
        isResendLoading,
        isBaasV2,
        resendPixKeyCodeV2,
        resendPixKeyCode,
        key,
        keyType,
        protocolId,
        onError,
        onResendSuccess,
        t,
    ]);
    return {
        isResendLoading,
        canResend,
        timeRemaining,
        handleResendCode,
    };
}
